import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';
import { firstUserErrorCode } from '@/graphql/errors';
import { CHANGE_PLAN } from '@/graphql/mutations/billing';
import type { PaidPlanId, SubscriptionData } from '../types';

export type ChangePlanOutcome =
  | { subscription: SubscriptionData; errorCode: null }
  | { subscription: null; errorCode: string };

type UseChangePlanResult = {
  changePlan: (plan: PaidPlanId) => Promise<ChangePlanOutcome>;
  isChanging: boolean;
};

export function useChangePlan(): UseChangePlanResult {
  const [mutate, { loading }] = useMutation(CHANGE_PLAN);

  const changePlan = useCallback(
    async (plan: PaidPlanId): Promise<ChangePlanOutcome> => {
      const { data } = await mutate({
        variables: { input: { plan, idempotencyKey: crypto.randomUUID() } },
      });
      const payload = data?.changePlan;
      if (payload?.subscription) return { subscription: payload.subscription, errorCode: null };
      return {
        subscription: null,
        errorCode: firstUserErrorCode(payload?.userErrors) ?? 'UNKNOWN',
      };
    },
    [mutate],
  );

  return { changePlan, isChanging: loading };
}
